import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Building2, ExternalLink, Globe, Lock, Palette, Scale } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { brandkitApi, brandsApi } from '@/features/brands/api';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { queryKeys } from '@/lib/api/queryKeys';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { toast } from '@/components/ui/Toast';
import { useFeatureAccess } from '@onxite/plan-sdk';
import { useT } from '@/lib/i18n/useT';
import { HelpHint } from '@/features/help/HelpHint';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import type { XkBrand } from '@/lib/api/types';

/**
 * PRD Xotski v2.0 §7.3 — Settings › Brand subsection.
 *
 * Surfaces the Brand Inventory opt-in toggle (PRD §9, per brand). The
 * plan-level featBrandInventory flag gates whether the toggle is
 * interactive — without the flag, the toggle is read-only and shows the
 * upgrade hint.
 *
 * Legal texts used to live here (migración 089). They moved to
 * Settings › Legal & Consent (`LegalSettingsPage`, `/settings/legal`), the
 * single page every app reads from; this page only points there.
 *
 * Per-brand toggling: each brand the user has visibility into has its
 * own row. Flipping it on enables the /co-brand-inventory endpoints
 * for that brand.
 */
export function BrandSettingsPage() {
  const t = useT();
  useFavoritable(t('settings.brand.title'));
  const qc = useQueryClient();
  const planBrandInventory = useFeatureAccess('brandInventory').enabled;

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.brands.all,
    queryFn: () => brandsApi.list(),
  });

  const publicPageMutation = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: { publicSlug?: string | null; publicPageOpen?: boolean } }) =>
      brandsApi.update(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.brands.all });
      toast({ tone: 'success', title: t('settings.brand.savedToast') });
    },
    onError: (e: Error) => {
      toast({ tone: 'error', title: t('settings.brand.errorToast'), description: e.message });
    },
  });
  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      brandsApi.update(id, { brandInventoryEnabled: enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.brands.all });
      toast({ tone: 'success', title: t('settings.brand.savedToast') });
    },
    onError: () => {
      toast({ tone: 'error', title: t('settings.brand.errorToast') });
    },
  });

  const brands = data?.items ?? [];

  return (
    <div className="page space-y-6">
      <Breadcrumbs
        items={[
          { label: t('breadcrumbs.settings'), to: '/settings' },
          { label: t('settings.brand.title') },
        ]}
      />

      <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-[var(--color-fg-primary)]">
          <Building2 className="h-5 w-5" />
          {t('settings.brand.title')}
          <HelpHint section="settings-brand" />
        </h1>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {t('settings.brand.subtitle')}
        </p>
      </header>

      {/*
        Perfiles de marca: la identidad de cada una (logotipo, paleta,
        tipografía) tal como la pinta el kit, con la puerta a Brand Studio.
        Antes esta página sólo tenía interruptores y quien buscaba «cambiar los
        colores de la marca» en Ajustes no encontraba por dónde.
      */}
      <Card className="p-5">
        <div className="flex items-center gap-2">
          <Palette className="h-4 w-4 text-[var(--color-accent-ink)]" />
          <h2 className="text-base font-semibold text-[var(--color-fg-primary)]">
            {t('settings.brand.profilesTitle')}
          </h2>
        </div>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {t('settings.brand.profilesDescription')}
        </p>
        <div className="mt-4 divide-y divide-[var(--color-border-subtle)]">
          {isLoading ? (
            <Skeleton className="h-14 w-full" />
          ) : brands.length === 0 ? (
            <p className="py-4 text-sm text-[var(--color-fg-muted)]">{t('settings.brand.noBrands')}</p>
          ) : (
            brands.map((brand) => <BrandProfileRow key={brand.id} brand={brand} />)
          )}
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-center gap-2">
          <Boxes className="h-4 w-4 text-[var(--color-accent-ink)]" />
          <h2 className="text-base font-semibold text-[var(--color-fg-primary)]">
            {t('settings.brand.inventoryTitle')}
          </h2>
          {!planBrandInventory && (
            <span className="ml-2 inline-flex items-center gap-1 rounded bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[10px] font-semibold uppercase text-[var(--color-fg-muted)]">
              <Lock className="h-3 w-3" /> {t('settings.brand.lockedBadge')}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {t('settings.brand.inventoryDescription')}
        </p>

        <div className="mt-4 divide-y divide-[var(--color-border-subtle)]">
          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : brands.length === 0 ? (
            <p className="py-4 text-sm text-[var(--color-fg-muted)]">
              {t('settings.brand.noBrands')}
            </p>
          ) : (
            brands.map((brand) => (
              <BrandToggleRow
                key={brand.id}
                brand={brand}
                interactive={planBrandInventory}
                onToggle={(enabled) =>
                  toggleMutation.mutate({ id: brand.id, enabled })
                }
                pending={toggleMutation.isPending}
              />
            ))
          )}
        </div>
      </Card>

      {/* CH-03 — página pública por marca: slug estable + interruptor. */}
      <Card className="p-5">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-[var(--color-accent-ink)]" />
          <h2 className="text-base font-semibold text-[var(--color-fg-primary)]">
            {t('settings.brand.publicPageTitle')}
          </h2>
        </div>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">{t('settings.brand.publicPageDescription')}</p>
        <div className="mt-4 divide-y divide-[var(--color-border-subtle)]">
          {(data?.items ?? []).map((brand) => (
            <BrandPublicPageRow
              key={brand.id}
              brand={brand}
              onSave={(patch) => publicPageMutation.mutate({ id: brand.id, patch })}
              pending={publicPageMutation.isPending}
            />
          ))}
        </div>
      </Card>

      {/*
        Los textos legales ya NO viven aquí. Están en Settings › Legal & Consent
        (`/settings/legal`, `co_company_legal`), que es la página que leen todas
        las aplicaciones. Esta fila se queda con identidad + inventario.
      */}
      <Card className="p-5">
        <div className="flex items-center gap-2">
          <Scale className="h-4 w-4 text-[var(--color-accent-ink)]" />
          <h2 className="text-base font-semibold text-[var(--color-fg-primary)]">
            {t('settings.legal.title')}
          </h2>
        </div>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">{t('settings.brand.legalMoved')}</p>
        <Link to="/settings/legal" className="mt-3 inline-block">
          <Button variant="secondary">{t('settings.brand.legalMovedCta')}</Button>
        </Link>
      </Card>
    </div>
  );
}

/**
 * Una marca con su identidad de un vistazo. El kit (`co_brandkit`) es lo que
 * de verdad pintan juegos, encuestas y carteles; la fila de `co_companies`
 * sólo sirve de respaldo para el logotipo y dos colores mientras no hay kit.
 */
function BrandProfileRow({ brand }: { brand: XkBrand }) {
  const t = useT();
  const kitQ = useQuery({
    queryKey: queryKeys.brandKit.byCompany(brand.id),
    queryFn: () => brandkitApi.get(brand.id),
    staleTime: 60_000,
  });
  const kit = kitQ.data ?? null;
  const logo = kit?.brandLogo ?? kit?.clientLogo ?? brand.logo ?? null;
  const swatches = [
    { key: 'primary', label: t('settings.brand.swatchPrimary'), value: kit?.primaryColor ?? brand.backgroundColor },
    { key: 'secondary', label: t('settings.brand.swatchSecondary'), value: kit?.secondaryColor ?? brand.secondaryColor },
    { key: 'accent', label: t('settings.brand.swatchAccent'), value: kit?.accentColor },
    { key: 'background', label: t('settings.brand.swatchBackground'), value: kit?.backgroundColor },
    { key: 'text', label: t('settings.brand.swatchText'), value: kit?.fontColor ?? brand.fontColor },
  ].filter((s): s is { key: string; label: string; value: string } => Boolean(s.value));
  const isRoot = brand.parentId == null;

  return (
    <div className="flex flex-wrap items-center gap-3 py-3">
      {logo ? (
        <img
          src={resolveImageUrl(logo)}
          alt=""
          className="h-10 w-10 flex-none rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] object-contain p-1"
        />
      ) : (
        <span
          aria-hidden
          className="grid h-10 w-10 flex-none place-items-center rounded-lg bg-[var(--color-bg-elevated)] text-sm font-extrabold text-[var(--color-fg-muted)]"
        >
          {(brand.name ?? '?').charAt(0).toUpperCase()}
        </span>
      )}

      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-sm font-semibold text-[var(--color-fg-primary)]">
          <span className="truncate">{brand.name}</span>
          {isRoot && (
            <span className="rounded bg-[var(--color-bg-elevated)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">
              {t('settings.brand.profileRoot')}
            </span>
          )}
        </p>
        <p className="truncate text-xs text-[var(--color-fg-muted)]">
          {kitQ.isLoading
            ? t('common.loading')
            : kit
              ? `${t('settings.brand.profileFont')}: ${kit.font} · ${brand.industry ?? t('settings.brand.profileNoIndustry')}`
              : t('settings.brand.profileNoKit')}
        </p>
      </div>

      {swatches.length > 0 && (
        <ul className="flex items-center gap-1" aria-label={t('settings.brand.swatchesLabel', { brand: brand.name })}>
          {swatches.map((s) => (
            <li
              key={s.key}
              title={`${s.label}: ${s.value}`}
              className="h-5 w-5 rounded-full border border-[var(--color-border-default)]"
              style={{ background: s.value }}
            />
          ))}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <Link to={`/brand-studio/${brand.id}`}>
          <Button variant="secondary" size="sm">
            <Palette className="mr-1.5 h-3.5 w-3.5" />
            {t('settings.brand.profileEdit')}
          </Button>
        </Link>
        <Link to={`/brand/${brand.id}`}>
          <Button variant="ghost" size="sm">
            {t('settings.brand.profileOpen')}
            <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

function BrandToggleRow({
  brand,
  interactive,
  onToggle,
  pending,
}: {
  brand: XkBrand;
  interactive: boolean;
  onToggle: (enabled: boolean) => void;
  pending: boolean;
}) {
  const enabled = !!brand.brandInventoryEnabled;

  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-medium text-[var(--color-fg-primary)]">
          {brand.name}
        </p>
        {brand.industry && (
          <p className="text-xs text-[var(--color-fg-muted)]">
            {brand.industry}
          </p>
        )}
      </div>
      <button
        type="button"
        aria-pressed={enabled}
        disabled={!interactive || pending}
        onClick={() => onToggle(!enabled)}
        className={[
          'relative h-6 w-11 rounded-full transition-colors',
          enabled
            ? 'bg-[var(--color-xk-accent)]'
            : 'bg-[var(--color-xk-surface-2)]',
          (!interactive || pending) && 'cursor-not-allowed opacity-60',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span
          className={[
            'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
            enabled ? 'translate-x-5' : 'translate-x-0.5',
          ].join(' ')}
        />
      </button>
    </div>
  );
}

/** Slug + interruptor de la página pública de una marca (CH-03). */
function BrandPublicPageRow({
  brand,
  onSave,
  pending,
}: {
  brand: XkBrand;
  onSave: (patch: { publicSlug?: string | null; publicPageOpen?: boolean }) => void;
  pending: boolean;
}) {
  const t = useT();
  const [slug, setSlug] = useState(brand.publicSlug ?? '');
  const open = !!brand.publicPageOpen;
  const saved = brand.publicSlug ?? '';
  const dirty = slug.trim().toLowerCase() !== saved;
  return (
    <div className="flex flex-wrap items-center gap-3 py-3">
      <div className="min-w-[160px] flex-1">
        <p className="text-sm font-medium text-[var(--color-fg-primary)]">{brand.name}</p>
        {saved ? (
          <a
            href={`/b/${saved}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-[var(--color-accent-ink)] hover:underline"
          >
            /b/{saved} <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          <p className="text-xs text-[var(--color-fg-muted)]">{t('settings.brand.publicPageNoSlug')}</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-[var(--color-fg-subtle)]">/b/</span>
        <input
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
          onBlur={() => dirty && onSave({ publicSlug: slug.trim() || null })}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          placeholder={t('settings.brand.publicPageSlugPlaceholder')}
          aria-label={t('settings.brand.publicPageSlug', { brand: brand.name })}
          disabled={pending}
          className="h-9 w-44 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-input)] px-2.5 text-sm text-[var(--color-fg-primary)] focus:border-[var(--color-accent-ink)] focus:outline-none"
        />
      </div>
      <label className="flex items-center gap-2 text-xs text-[var(--color-fg-muted)]">
        <button
          type="button"
          role="switch"
          aria-checked={open}
          aria-label={t('settings.brand.publicPageOpen', { brand: brand.name })}
          disabled={pending || !saved}
          onClick={() => onSave({ publicPageOpen: !open })}
          className={[
            'relative h-6 w-11 rounded-full transition-colors',
            open ? 'bg-[var(--color-xk-accent)]' : 'bg-[var(--color-xk-surface-2)]',
            (pending || !saved) && 'cursor-not-allowed opacity-60',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <span
            className={[
              'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
              open ? 'translate-x-5' : 'translate-x-0.5',
            ].join(' ')}
          />
        </button>
        {open ? t('settings.brand.publicPageIsOpen') : t('settings.brand.publicPageIsGated')}
      </label>
    </div>
  );
}
